import React, { useEffect, useState, useRef } from 'react';
import { getConversions, getDailyTotals } from '../api';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import CustomTooltip from '../components/CustomTooltip';
import { formatTableDate, formatTimeWithSeconds } from '../utils/formatDate';

export default function Conversions({ refreshKey }) {
  // Lazy initialization: read cached data (if any) synchronously.
  const [conversions, setConversions] = useState(() => {
    const cached = localStorage.getItem('conversionsData');
    return cached ? JSON.parse(cached) : [];
  });
  const [dailyTotals, setDailyTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(() => {
    const cached = localStorage.getItem('conversionsData');
    return cached ? JSON.parse(cached).length : 0;
  });
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef(null);
  const limit = 30;
  // Auto-refresh state to trigger effect every 5 seconds.
  const [autoRefreshKey, setAutoRefreshKey] = useState(0);

  // Persist conversions data in localStorage whenever it changes.
  useEffect(() => {
    localStorage.setItem('conversionsData', JSON.stringify(conversions));
  }, [conversions]);

  async function loadConversions(currentOffset = offset) {
    if (currentOffset !== 0 && !hasMore) return;
    setLoading(true);
    try {
      const data = await getConversions(currentOffset);
      if (data.length > 0) {
        setConversions(prev => [...prev, ...data]);
        setOffset(currentOffset + data.length);
        if (data.length < limit) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch conversions:', error);
    } finally {
      setLoading(false);
    }
  }

  // Combined refresh effect (manual via refreshKey and auto via autoRefreshKey)
  // that fetches the first page and merges in new rows without clearing the table.
  useEffect(() => {
    async function fetchLatest() {
      try {
        const newData = await getConversions(0);
        setConversions(prev => {
          const existingIds = new Set(prev.map(item => item.conversion_id));
          const newItems = newData.filter(item => !existingIds.has(item.conversion_id));
          if (newItems.length > 0) {
            setOffset(oldOffset => oldOffset + newItems.length);
            return [...newItems, ...prev];
          }
          return prev;
        });
      } catch (error) {
        console.error('Failed to fetch latest conversions:', error);
      }
    }
    fetchLatest();
  }, [autoRefreshKey, refreshKey]);

  // Fetch daily totals for conversions (last 30 days)
  useEffect(() => {
    async function fetchDailyTotals() {
      try {
        const totals = await getDailyTotals('conversions');
        setDailyTotals(totals);
      } catch (error) {
        console.error('Error fetching daily totals:', error);
      }
    }
    fetchDailyTotals();
  }, []);

  // Auto-refresh interval: update autoRefreshKey every 5 seconds.
  useEffect(() => {
    const intervalId = setInterval(() => {
      setAutoRefreshKey(prev => prev + 1);
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  function lastConversionRef(node) {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        console.log('Last conversion element is visible, loading more...');
        loadConversions(offset);
      }
    });
    if (node) observer.current.observe(node);
  }

  return (
    <div className="conversions-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Conversion Logs</h2>
        <div style={{ width: '300px', height: '50px' }}>
          {dailyTotals.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyTotals}>
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="rgb(234, 88, 12)" 
                  strokeWidth={2} 
                  dot={false} 
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th style={{ width: '120px' }}>Started</th>
              <th style={{ width: '120px' }}>Completed</th>
              <th>Status</th>
              <th>Duration</th>
              <th className="file-cell">File Path</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {conversions.map((conv, index) => (
              <tr key={conv.conversion_id} ref={index === conversions.length - 1 ? lastConversionRef : null}>
                <td>{conv.conversion_id}</td>
                <td title={formatTimeWithSeconds(conv.started_at)}>
                  {formatTableDate(conv.started_at)}
                </td>
                <td title={formatTimeWithSeconds(conv.completed_at)}>
                  {formatTableDate(conv.completed_at)}
                </td>
                <td>
                  <span className={`status ${conv.status}`} style={{ margin: 0 }}>
                    {conv.status}
                  </span>
                </td>
                <td>{conv.duration} ms</td>
                <td className="file-cell" title={conv.converted_file_path}>
                  {conv.converted_file_path}
                </td>
                <td className="error-cell" title={conv.error_message || '-'}>
                  {conv.error_message || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hasMore && <p className="no-more-logs">No more logs to load.</p>}
    </div>
  );
}
